import { useCallback, useEffect, useState } from "react";
import { evaluateGeofence, SERVICE_CENTER, type GeofenceResult } from "./geofence";
import { listQueue, queueCount } from "./store";
import { syncQueue, type SyncResult } from "./sync";

/**
 * Field-journey hooks: online status, queue state + sync, GPS capture.
 */

/** Live online/offline status (navigator.onLine + window events). */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(() => navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

/** Pending offline-queue count + replay on reconnect. */
export function useOfflineQueue(online: boolean) {
  const [pending, setPending] = useState<number>(() => queueCount());
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<SyncResult | null>(null);

  const refresh = useCallback(() => setPending(queueCount()), []);

  const sync = useCallback(async () => {
    if (!navigator.onLine || listQueue().length === 0) {
      refresh();
      return;
    }
    setSyncing(true);
    try {
      const result = await syncQueue();
      setLastSync(result);
    } finally {
      setSyncing(false);
      refresh();
    }
  }, [refresh]);

  useEffect(() => {
    if (online) void sync();
  }, [online, sync]);

  useEffect(() => {
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [refresh]);

  return { pending, syncing, lastSync, sync, refresh };
}

export type GpsCaptureState =
  | { phase: "idle" }
  | { phase: "acquiring" }
  | { phase: "captured"; lat: number; lng: number; accuracyM: number | null; geofence: GeofenceResult }
  | { phase: "error"; message: string };

/** Browser Geolocation capture with the 3 geofence states from handoff TASK-002 S1. */
export function useGpsCapture() {
  const [state, setState] = useState<GpsCaptureState>({ phase: "idle" });

  const capture = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setState({ phase: "error", message: "GPS is not available on this device" });
      return;
    }
    setState({ phase: "acquiring" });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        setState({
          phase: "captured",
          lat: latitude,
          lng: longitude,
          accuracyM: Number.isFinite(accuracy) ? Math.round(accuracy) : null,
          geofence: evaluateGeofence(latitude, longitude),
        });
      },
      (err) => {
        const message =
          err.code === err.PERMISSION_DENIED
            ? "Location permission denied — enable GPS and retry"
            : "Unable to acquire GPS — move to open sky and retry";
        setState({ phase: "error", message });
      },
      { enableHighAccuracy: true, timeout: 20_000, maximumAge: 0 },
    );
  }, []);

  const reset = useCallback(() => setState({ phase: "idle" }), []);

  return { state, capture, reset, fenceLabel: SERVICE_CENTER.label, fenceRadiusM: SERVICE_CENTER.radiusM };
}
